import { Slider as SliderPrimitive } from "radix-ui";

/**
 * The rating control — EPIC-061 WI-220.
 *
 * A slider across the scale points, with no rating pre-selected (AC-625): an
 * untouched question has to be distinguishable from a deliberate low score.
 * Radix always needs a value, so the track runs from 0 to the top rung and
 * the thumb is hidden while nothing is chosen.
 *
 * Position 0 is "N/A" when the scale has one — the first stop on the line,
 * before 1, rather than a separate button under it (Matt, 2026-10-21). Without
 * an N/A rung, 0 is the bare "nothing chosen" slot and sliding back to it
 * clears the answer. Either way a click on rung 1 is an actual change of
 * value, which a 1-to-5 track starting on 1 would swallow.
 *
 * The numbered stops underneath are buttons as well as labels: they let a
 * reviewer read another rung's wording without committing to it (WI-312) and
 * click the chosen one again to clear it. The chosen rung's name is spoken
 * through `aria-valuetext`, so the value is more than a bare number (AC-626).
 */
/**
 * Where a stop sits along the track. Radix keeps the thumb wholly inside the
 * track, so its centre is inset by up to half its width at either end; the
 * labels need the same inset or they drift off the thumb towards the ends.
 * 24px is the thumb's `size-6`.
 */
const THUMB_PX = 24;

function stopPosition(fraction: number): string {
  return `calc(${fraction * 100}% + ${THUMB_PX / 2 - fraction * THUMB_PX}px)`;
}

export function RatingSlider({
  rungs,
  notApplicable,
  value,
  onChange,
  onPreview,
  disabled,
  label,
  describedBy,
}: {
  /** Scale points the slider spans, N/A excluded, in order. */
  rungs: { ordinal: number; name: string }[];
  /** The scale's N/A rung, placed at the start of the line; null if it has none. */
  notApplicable: { ordinal: number; name: string } | null;
  /** The chosen ordinal — a rung, the N/A ordinal, or null for nothing chosen. */
  value: number | null;
  onChange: (value: number | null) => void;
  /** The rung being looked at, or null when attention leaves it. */
  onPreview: (ordinal: number | null) => void;
  disabled: boolean;
  label: string;
  describedBy: string;
}) {
  const top = rungs.reduce((max, r) => Math.max(max, r.ordinal), 0);
  const isNotApplicable = notApplicable !== null && value === notApplicable.ordinal;
  const chosen = isNotApplicable ? null : (rungs.find((r) => r.ordinal === value) ?? null);
  const unset = chosen === null && !isNotApplicable;
  const spoken = isNotApplicable ? notApplicable : chosen;
  const position = chosen?.ordinal ?? 0;

  // Every stop on the line, N/A first. Its slot is position 0.
  const stops = [
    ...(notApplicable ? [{ ordinal: notApplicable.ordinal, name: notApplicable.name, at: 0 }] : []),
    ...rungs.map((r) => ({ ordinal: r.ordinal, name: r.name, at: r.ordinal })),
  ];

  // The coloured fill runs from 1 to the chosen rung. It deliberately starts at
  // 1, not at N/A: a 4 is not "N/A plus four".
  const lowest = rungs[0]?.ordinal ?? 1;
  const fill =
    chosen && top > 0
      ? {
          left: stopPosition(lowest / top),
          width: `calc(${((chosen.ordinal - lowest) / top) * 100}% - ${((chosen.ordinal - lowest) / top) * THUMB_PX}px)`,
        }
      : null;

  return (
    <div className="px-3 pt-3 pb-1">
      <SliderPrimitive.Root
        min={0}
        max={top}
        step={1}
        value={[position]}
        disabled={disabled}
        onValueChange={([next]) => {
          if (next === undefined) return;
          if (next === 0) {
            onChange(notApplicable ? notApplicable.ordinal : null);
            return;
          }
          // A value that is not a rung is ignored.
          onChange(rungs.find((r) => r.ordinal === next)?.ordinal ?? null);
        }}
        className="relative flex h-8 w-full touch-none select-none items-center data-[disabled]:opacity-60"
      >
        <SliderPrimitive.Track className="relative h-3 grow rounded-full bg-accent ring-1 ring-border ring-inset">
          {/* Radix's own range would start at N/A; the fill below starts at 1. */}
          <SliderPrimitive.Range className="absolute h-full" />
          {fill && (
            <span
              aria-hidden
              className="absolute inset-y-0 rounded-full bg-gradient-to-r from-chart-1 to-primary"
              style={fill}
            />
          )}
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label={label}
          aria-describedby={describedBy}
          aria-valuetext={spoken ? `${spoken.name}` : "No rating chosen"}
          onFocus={() => onPreview(spoken?.ordinal ?? null)}
          onBlur={() => onPreview(null)}
          className={`block size-6 rounded-full border-4 border-background bg-primary shadow-md ring-2 ring-primary transition-opacity focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring ${
            unset ? "opacity-0 focus-visible:opacity-100" : ""
          }`}
        />
      </SliderPrimitive.Root>

      <div className="relative mt-2 h-10" role="presentation">
        {stops.map((stop) => {
          const selected = stop.ordinal === value;
          const isNa = notApplicable !== null && stop.ordinal === notApplicable.ordinal;
          return (
            <button
              key={stop.ordinal}
              type="button"
              tabIndex={-1}
              disabled={disabled}
              aria-label={isNa ? stop.name : `${stop.ordinal}: ${stop.name}`}
              aria-pressed={selected}
              onClick={() => onChange(selected ? null : stop.ordinal)}
              onMouseEnter={() => onPreview(stop.ordinal)}
              onMouseLeave={() => onPreview(null)}
              style={{ left: stopPosition(top > 0 ? stop.at / top : 0) }}
              className={`absolute flex h-9 min-w-9 -translate-x-1/2 items-center justify-center rounded-full border-2 px-2 font-semibold tabular-nums transition-colors disabled:cursor-not-allowed ${
                isNa ? "text-xs" : "text-base"
              } ${
                selected
                  ? "border-primary bg-primary text-primary-foreground shadow-sm"
                  : "border-border bg-background text-foreground hover:border-primary hover:bg-accent"
              }`}
            >
              {isNa ? stop.name : stop.ordinal}
            </button>
          );
        })}
      </div>
    </div>
  );
}
